import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Database, Package, FilePdf, FileXls } from "@phosphor-icons/react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Carton Qty is always treated as a whole number - you can't order half a carton.
function toIntQty(value) {
  return Math.floor(toNumber(value));
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function QuotePage() {
  const { token } = useParams();
  const { toast } = useToast();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  // cartonQty[variant_record_id] = number entered by whoever is viewing this page - never
  // sent back to the server for persistence, purely local state used for the live calculation
  // below (it IS sent, transiently, to the export endpoints below so the downloaded PDF/Excel
  // matches what's on screen - but that export request is never written to any database).
  const [cartonQty, setCartonQty] = useState({});

  useEffect(() => {
    api
      .get(`/quotes/${token}`)
      .then(({ data }) => setQuote(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Quote not found or has expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleCartonChange = (variantRecordId, value) => {
    setCartonQty((prev) => ({ ...prev, [variantRecordId]: value }));
  };

  const hasGtSku = useMemo(
    () => !!quote?.items?.some((i) => (i.variants || []).some((v) => v.gt_sku)),
    [quote]
  );

  const rows = useMemo(() => {
    if (!quote) return [];
    const out = [];
    for (const item of quote.items) {
      const variants = item.variants && item.variants.length ? item.variants : [{}];
      // CBM is per-variant (each size has its own box dimensions -> its own cbm_per_carton),
      // so the item's total CBM is the sum across all its variants, not one shared value.
      let itemTotalCbm = 0;
      variants.forEach((v) => {
        const cartons = toIntQty(cartonQty[v.variant_record_id]);
        itemTotalCbm += toNumber(v.cbm_per_carton) * cartons;
      });
      variants.forEach((v, i) => {
        const cartons = toIntQty(cartonQty[v.variant_record_id]);
        const totalQty = cartons * Math.round(toNumber(v.packing_qty));
        const totalAmount = totalQty * toNumber(v.price);
        const totalCbm = i === 0 ? itemTotalCbm : 0;
        out.push({
          key: v.variant_record_id || `${item.main_product_record_id}-${i}`,
          item,
          v,
          isFirst: i === 0,
          rowSpan: variants.length,
          cartons,
          totalQty,
          totalAmount,
          totalCbm,
        });
      });
    }
    return out;
  }, [quote, cartonQty]);

  const grandTotalAmount = rows.reduce((sum, r) => sum + r.totalAmount, 0);
  const grandTotalCbm = rows.reduce((sum, r) => sum + r.totalCbm, 0);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await api.post(
        `/quotes/${token}/export/${format}`,
        { carton_qty: cartonQty },
        { responseType: "blob" }
      );
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `quote.${format === "excel" ? "xlsx" : "pdf"}`;
      downloadBlob(new Blob([response.data]), filename);
    } catch (e) {
      toast({
        title: "Export failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white text-sm text-zinc-500" data-testid="quote-page-loading">
        Loading quote...
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 bg-white text-center" data-testid="quote-page-error">
        <Package size={32} className="text-zinc-300" />
        <p className="text-sm text-zinc-500">{error || "Quote not found"}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white" data-testid="quote-page">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-300 px-4 py-3 lg:px-6">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
            <Database size={16} weight="bold" className="text-blue-600" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Quote Calculator</h1>
            <p className="text-xs text-zinc-500" data-testid="quote-customer-name">
              Customer: {quote.customer_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={exporting !== null}
            className="rounded-sm border-zinc-300"
            onClick={() => handleExport("pdf")}
            data-testid="quote-export-pdf-button"
          >
            <FilePdf size={14} className="mr-1.5" /> {exporting === "pdf" ? "..." : "Export PDF"}
          </Button>
          <Button
            size="sm"
            disabled={exporting !== null}
            className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => handleExport("excel")}
            data-testid="quote-export-excel-button"
          >
            <FileXls size={14} className="mr-1.5" /> {exporting === "excel" ? "..." : "Export Excel"}
          </Button>
        </div>
      </header>

      <div className="p-4 lg:p-6">
        <p className="mb-4 max-w-2xl text-xs text-zinc-500" data-testid="quote-disclaimer">
          Fill in a carton quantity for any product below - the total amount and CBM are
          calculated automatically in your browser. Nothing you enter here is saved back to
          Better Home Fashion Inc's system (only the PDF/Excel file you explicitly export is
          generated for you to download), so feel free to forward this link to anyone who needs
          to calculate their own numbers.
        </p>

        <div className="min-w-0 overflow-x-auto rounded-sm border border-zinc-300">
          <table className="w-full min-w-[1040px] border-collapse text-sm" data-testid="quote-items-table">
            <colgroup>
              <col style={{ width: "110px" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "90px" }} />
              <col style={{ width: "70px" }} />
              {hasGtSku && <col style={{ width: "90px" }} />}
              <col style={{ width: "80px" }} />
              <col style={{ width: "70px" }} />
              <col style={{ width: "80px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "90px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "80px" }} />
            </colgroup>
            <thead>
              <tr className="bg-zinc-950 text-left text-[11px] uppercase tracking-[0.05em] text-white">
                <th className="px-3 py-2">Photo</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Item No</th>
                <th className="px-3 py-2">Size</th>
                {hasGtSku && <th className="px-3 py-2">GT SKU</th>}
                <th className="px-3 py-2 text-center">Inventory Qty</th>
                <th className="px-3 py-2 text-center">Pcs/CTN</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-3 py-2 text-center">Carton Qty</th>
                <th className="px-3 py-2 text-right">Total Qty</th>
                <th className="px-3 py-2 text-right">Total Amount</th>
                <th className="px-3 py-2 text-right">CBM</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-zinc-200 last:border-0">
                  {r.isFirst && (
                    <td className="px-3 py-2 align-top" rowSpan={r.rowSpan}>
                      <img
                        src={`${api.defaults.baseURL}/quotes/${token}/photo/${r.item.main_product_record_id}`}
                        alt={r.item.description}
                        className="h-[84px] w-[84px] rounded-sm object-contain"
                        onError={(e) => {
                          e.target.style.visibility = "hidden";
                        }}
                        data-testid={`quote-item-photo-${r.item.main_product_record_id}`}
                      />
                    </td>
                  )}
                  {r.isFirst && (
                    <td className="px-3 py-2 align-top break-words" rowSpan={r.rowSpan}>
                      {r.item.description || "-"}
                    </td>
                  )}
                  {r.isFirst && (
                    <td className="px-3 py-2 align-top font-bold" rowSpan={r.rowSpan}>
                      {r.item.item_no || "-"}
                    </td>
                  )}
                  <td className="px-3 py-2">{r.v.size || "-"}</td>
                  {hasGtSku && <td className="px-3 py-2">{r.v.gt_sku || "-"}</td>}
                  <td className="px-3 py-2 text-center" data-testid={`quote-inventory-qty-${r.v.variant_record_id}`}>
                    {r.v.inventory_qty !== null && r.v.inventory_qty !== undefined && r.v.inventory_qty !== ""
                      ? Math.round(toNumber(r.v.inventory_qty))
                      : "-"}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.v.packing_qty !== null && r.v.packing_qty !== undefined ? Math.round(toNumber(r.v.packing_qty)) : "-"}
                  </td>
                  <td className="px-3 py-2 text-right">${toNumber(r.v.price).toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={cartonQty[r.v.variant_record_id] ?? ""}
                      onChange={(e) => handleCartonChange(r.v.variant_record_id, e.target.value)}
                      className="h-8 w-20 rounded-sm border-zinc-300 text-center"
                      data-testid={`quote-carton-qty-input-${r.v.variant_record_id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`quote-total-qty-${r.v.variant_record_id}`}>
                    {r.totalQty || 0}
                  </td>
                  <td className="px-3 py-2 text-right font-bold" data-testid={`quote-total-amount-${r.v.variant_record_id}`}>
                    ${r.totalAmount.toFixed(2)}
                  </td>
                  {r.isFirst && (
                    <td className="px-3 py-2 text-right align-top" rowSpan={r.rowSpan} data-testid={`quote-item-cbm-${r.item.main_product_record_id}`}>
                      {r.totalCbm ? r.totalCbm.toFixed(3) : "-"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col items-end gap-1 text-sm">
          <div data-testid="quote-grand-total-cbm">
            Total CBM: <span className="font-bold">{grandTotalCbm.toFixed(3)} m³</span>
          </div>
          <div className="text-lg" data-testid="quote-grand-total-amount">
            Grand Total: <span className="font-bold text-blue-600">${grandTotalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
